import { initPostHog } from '@kiku/analytics/client';
import { frontendConfig } from '@kiku/config/env/frontend';
import { scrubPiiFields } from '@kiku/security/scrub-pii';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import '~/lib/i18n';

import { queryClient } from '~/lib/query-client';
import { routeTree } from '~/routeTree.gen';

import '~/styles/globals.css';

Sentry.init({
  dsn: frontendConfig.VITE_SENTRY_DSN,
  environment: frontendConfig.VITE_SENTRY_ENVIRONMENT ?? frontendConfig.MODE,
  release: frontendConfig.VITE_SENTRY_RELEASE,
  enabled: frontendConfig.MODE === 'production',
  sampleRate: 1.0,
  tracesSampleRate: 0,
  beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
    if (event.request?.headers) {
      const headers = { ...event.request.headers };
      for (const h of ['authorization', 'cookie', 'x-workos-session']) {
        if (h in headers) headers[h] = '[Redacted]';
      }
      event.request.headers = headers;
    }
    if (
      event.request?.data &&
      typeof event.request.data === 'object' &&
      !Array.isArray(event.request.data)
    ) {
      event.request.data = scrubPiiFields(event.request.data as Record<string, unknown>);
    }
    return event;
  }
});

initPostHog();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreloadStaleTime: 0
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
